

"use client";

import { useMemo, useState, useEffect, useCallback } from "react";
import { useAuth } from "react-oidc-context";

/* Data */
import { colleges, majorsByCategory } from "../../data/counselorData";
import {
  achievementOptions,
  hardSkillOptions,
  softSkillOptions,
  reasonSuggestions,
} from "../../data/counselorSuggestions";
import { TEMPLATE_CATALOG } from "../../data/lorTemplates";
import { AI_EXPERIENCE_PRESETS } from "../../data/aiExperiencePresets";
import { SPORT_OPTIONS } from "../../data/sportOptions";

/* Shared components & utils (point to ../components) */
import { applyTemplateShell } from "../../components/utils/templateUtils";
import PreviewSection from "../../components/form-sections/PreviewSection";

/* History utils */
import {
  TRACK_KEYS,
  storageKey,
  pushToHistoryArray,
} from "../../components/utils/historyUtils";
import { HistoryDatalist } from "../../components/ui/HistoryDatalist";

/* Local UI + Sections */
import Spinner from "../../components/ui/Spinner";
import Field from "../../components/ui/Field";
import TemplatePicker from "../../components/form-sections/TemplatePicker";
import SavedLettersSection from "../../components/form-sections/SavedLettersSection";
import UniversalHeaderSection from "../../components/form-sections/UniversalHeaderSection";
import CounselorContactSection from "../../components/form-sections/CounselorContactSection";
import StudentInstitutionSection from "../../components/form-sections/StudentInstitutionSection";
import RecipientSection from "../../components/form-sections/RecipientSection";
import ExperienceSection from "../../components/form-sections/EvidenceExperienceSection";
import ClosingSection from "../../components/form-sections/ClosingSection";


/* Letter + parsing utils */
import {
  toLower,
  uniqKeepOrder,
  parseComma,
  parseSemicolon,
  parseReasons,
  intersectOptions,
  injectExperienceIntoLetter,
  removeExperienceFromLetter,
  injectSportIntoLetter,
  base64ToBlob,
  loadCustomList,
  saveCustomList,
  addEndorsementTail,
} from "../../components/utils/letterUtils";

/* ============================== Constants ============================== */
const CUSTOM_KEYS = {
  achievements: "cb_custom_achievements_v1",
  hard_skills: "cb_custom_hard_skills_v1",
  soft_skills: "cb_custom_soft_skills_v1",
  reasons: "cb_custom_reasons_v1",
};

export default function CounselorBuilderPage() {
  const auth = useAuth();

  const todayLong = useMemo(
    () =>
      new Date().toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      }),
    []
  );

  /* API Logic using useAuth context */
  const list = useCallback(async () => {
    if (!auth.isAuthenticated || !auth.user?.access_token) {
      throw new Error("Cannot fetch letters: User is not authenticated.");
    }
    const token = auth.user.access_token;
    const res = await fetch("/api/letters", {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
    if (!res.ok) {
      const errorBody = await res.json();
      throw new Error(errorBody.error || "Failed to fetch letters");
    }
    const data = await res.json();
    return data.letters;
  }, [auth.isAuthenticated, auth.user]);

  const add = useCallback(
    async (letterData) => {
      if (!auth.isAuthenticated || !auth.user?.access_token) {
        throw new Error("Cannot save letter: User is not authenticated.");
      }
      const token = auth.user.access_token;
      const res = await fetch("/api/letters", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(letterData),
      });
      if (!res.ok) {
        const errorBody = await res.json();
        throw new Error(errorBody.error || "Failed to save letter");
      }
      const data = await res.json();
      return data.id;
    },
    [auth.isAuthenticated, auth.user]
  );

  const remove = useCallback(
    async (letterId) => {
      if (!auth.isAuthenticated || !auth.user?.access_token) {
        throw new Error("Cannot delete letter: User is not authenticated.");
      }
      const token = auth.user.access_token;
      const res = await fetch(`/api/letters?id=${letterId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!res.ok) {
        const errorBody = await res.json();
        throw new Error(errorBody.error || "Failed to delete letter");
      }
    },
    [auth.isAuthenticated, auth.user]
  );

  /* ------------------------------ History state ------------------------------ */
  const [history, setHistory] = useState({});
  useEffect(() => {
    const obj = {};
    TRACK_KEYS.forEach((k) => {
      try {
        obj[k] = JSON.parse(localStorage.getItem(storageKey(k)) || "[]");
      } catch {
        obj[k] = [];
      }
    });
    setHistory(obj);
  }, []);

  const pushHistory = (key, value) => {
    setHistory((prev) => {
      const current = prev[key] || [];
      const next = pushToHistoryArray(current, value);
      try {
        localStorage.setItem(storageKey(key), JSON.stringify(next));
      } catch {}
      return { ...prev, [key]: next };
    });
  };

  /* ------------------------------ Form state ------------------------------ */
  const [form, setForm] = useState({
    format: "txt",
    header_line1: "",
    header_line2: "",
    header_line3: "",
    header_addr1: "",
    header_addr2: "",
    header_greeting: "Program Director",
    header_logoDataUrl: "",
    header_logoAlign: "left",
    header_logoWidth: 140,
    date: "",
    counselor_name: "",
    counselor_title: "",
    school: "",
    email: "",
    phone: "",
    address1: "",
    address2: "",
    student: "",
    college: "",
    program: "",
    relationship: "",
    time_known: "",
    resume_on_file: false,
    meeting_notes: "",
    mission: "",
    values: "",
    alignment: "",
    achievements: "",
    hard_skills: "",
    soft_skills: "",
    reasons: "",
    story: "",
    rec_strength: "strongly",
    strength_rating: 3,
    need_reason: "",
    experience: "",
    include_experience: true,
    ai_experience_prompt: "",
    closing: "",
    signature: "",
    sport: "",
    recipient_name: "",
    recipient_title: "",
    recipient_org: "",
    recipient_addr1: "",
    recipient_addr2: "",
    recipient_salutation: "",
  });

  useEffect(() => {
    setForm((f) => (f.date ? f : { ...f, date: todayLong }));
  }, [todayLong]);

  const [selectedTemplates, setSelectedTemplates] = useState(["admissions"]);
  const [activeTemplateId, setActiveTemplateId] = useState("admissions");
  const [letters, setLetters] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiGlow, setAiGlow] = useState(false);
  const [savedAt, setSavedAt] = useState(0);

  const setField = (k) => (e) =>
    setForm((f) => ({
      ...f,
      [k]:
        e?.target?.type === "checkbox"
          ? e.target.checked
          : e?.target?.value ?? e,
    }));

  const [achievementOpts, setAchievementOpts] = useState(achievementOptions);
  const [hardOpts, setHardOpts] = useState(hardSkillOptions);
  const [softOpts, setSoftOpts] = useState(softSkillOptions);
  const [reasonOpts, setReasonOpts] = useState(reasonSuggestions);

  useEffect(() => {
    const ach = uniqKeepOrder([...achievementOptions, ...loadCustomList(CUSTOM_KEYS.achievements)]);
    const hard = uniqKeepOrder([...hardSkillOptions, ...loadCustomList(CUSTOM_KEYS.hard_skills)]);
    const soft = uniqKeepOrder([...softSkillOptions, ...loadCustomList(CUSTOM_KEYS.soft_skills)]);
    const reas = uniqKeepOrder([...reasonSuggestions, ...loadCustomList(CUSTOM_KEYS.reasons)]);
    setAchievementOpts(ach);
    setHardOpts(hard);
    setSoftOpts(soft);
    setReasonOpts(reas);
  }, []);

  const addCustomOption = (listKey, value) => {
    const v = String(value || "").trim();
    if (!v) return;
    const map = {
      achievements: [achievementOpts, setAchievementOpts, CUSTOM_KEYS.achievements, achievementOptions],
      hard_skills: [hardOpts, setHardOpts, CUSTOM_KEYS.hard_skills, hardSkillOptions],
      soft_skills: [softOpts, setSoftOpts, CUSTOM_KEYS.soft_skills, softSkillOptions],
      reasons: [reasonOpts, setReasonOpts, CUSTOM_KEYS.reasons, reasonSuggestions],
    };
    const entry = map[listKey];
    if (!entry) return;
    const [opts, setter, storageKeyName, baseDefaults] = entry;
    const next = uniqKeepOrder([...(opts || []), v]);
    setter(next);
    const baseSet = new Set((baseDefaults || []).map(toLower));
    const customOnly = next.filter((x) => !baseSet.has(toLower(x)));
    saveCustomList(storageKeyName, customOnly);
  };

  const [savedLettersList, setSavedLettersList] = useState([]);
  const [selectedSavedId, setSelectedSavedId] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!auth.isAuthenticated) return;
        const letters = await list();
        if (!cancelled) setSavedLettersList(letters);
      } catch (e) {
        console.error(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [auth.isAuthenticated, list]);

  function setLetterText(tid, newText, markEdited = true) {
    setLetters((prev) => {
      const curr = prev[tid] || { text: "", orig: "" };
      const edited = markEdited ? newText !== curr.orig : curr.edited;
      const docxBlob = edited ? null : curr.docxBlob || null;
      return { ...prev, [tid]: { ...curr, text: newText, edited, docxBlob } };
    });
  }

  function revertActive() {
    setLetters((prev) => {
      const curr = prev[activeTemplateId];
      if (!curr) return prev;
      return {
        ...prev,
        [activeTemplateId]: { ...curr, text: curr.orig || "", edited: false, docxBlob: curr.docxBlob || null },
      };
    });
  }

  const currentLetter = () => letters[activeTemplateId]?.text || "";
  const currentDocx = () => letters[activeTemplateId]?.docxBlob || null;
  const isCurrentEdited = () => !!letters[activeTemplateId]?.edited;

  /* ------------------------------ Save current letter (DynamoDB) ------------------------------ */
  function onSaveLetter() {
    (async () => {
      try {
        if (!auth.isAuthenticated) {
          setError("Please sign in to save letters.");
          return;
        }
        const text = currentLetter();
        if (!text) return;

        const id = await add({
          student: form.student,
          school: form.school,
          sport: form.sport,
          degree: form.program,
          college: form.college,
          templateId: activeTemplateId,
          text,
          date: form.date,
          rating: form.strength_rating,
        });

        const fresh = await list();
        setSavedLettersList(fresh);
        setSelectedSavedId(id);
        setSavedAt(Date.now());
        setTimeout(() => setSavedAt(0), 1500);
      } catch {
        setError("Failed to save letter.");
      }
    })();
  }

  function loadSavedLetterById(id) {
    const item = (savedLettersList || []).find((x) => x.id === id);
    if (!item) return;
    const tplId = item.templateId || "admissions";
    setLetters({
      [tplId]: { text: item.text || "", orig: item.text || "", edited: false, docxBlob: null },
    });
    setActiveTemplateId(tplId);
    setForm((f) => ({
      ...f,
      student: item.student || f.student,
      college: item.college || f.college,
      program: item.degree || f.program,
      school: item.school || f.school,
      sport: item.sport || f.sport,
      date: item.date || f.date,
      strength_rating: typeof item.rating === "number" ? item.rating : f.strength_rating,
    }));
    setSelectedSavedId(id);
  }

  async function onBuild(e) {
    e.preventDefault();
    setLoading(true);
    setError("");
    setLetters({});

    try {
      const payloadBase = {
        ...form,
        experience: addEndorsementTail(form.experience, {
          studentName: form.student,
          college: form.college,
          program: form.program,
        }),
        formatStyle: "college-admissions-lor",
        perspective: "high-school-counselor",
        exportOptions: { embedLogoInDocx: true, embedLogoInPdf: true },
      };

      const chosen = selectedTemplates.length ? selectedTemplates : ["admissions"];

      const results = await Promise.all(
        chosen.map(async (tplId) => {
          const res = await fetch("/api/counselor-rec", {
            method: "POST",
            headers: { "content-type": "application/json" },
            body: JSON.stringify(payloadBase),
          });
          const data = await res.json();
          if (!res.ok) throw new Error(data.error || "Failed to build the letter");

          const tpl = TEMPLATE_CATALOG.find((t) => t.id === tplId) || TEMPLATE_CATALOG[0];
          const rawBody = (data.letterText || "").trim();
          const baseline = applyTemplateShell(rawBody, tpl, form, todayLong);

          let previewText = baseline;
          const expWithTail = addEndorsementTail(form.experience, {
            studentName: form.student,
            college: form.college,
            program: form.program,
          });
          previewText = form.include_experience
            ? injectExperienceIntoLetter(previewText, expWithTail, form)
            : removeExperienceFromLetter(previewText, expWithTail);
          previewText = injectSportIntoLetter(previewText, form.sport, form.student);

          const docxBlob = data.docxBase64 ? base64ToBlob(data.docxBase64) : null;

          return [
            tplId,
            { text: previewText, orig: baseline, edited: previewText !== baseline, docxBlob },
          ];
        })
      );

      setLetters(Object.fromEntries(results));
      const first = chosen[0];
      setActiveTemplateId((id) => (chosen.includes(id) ? id : first));
    } catch (err) {
      setError(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  }

  async function aiExperience() {
    setAiBusy(true);
    setError("");
    try {
      const res = await fetch("/api/counselor-rec?mode=experience", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          student: form.student,
          college: form.college,
          program: form.program,
          sport: form.sport,
          relationship: form.relationship,
          time_known: form.time_known,
          achievements: form.achievements,
          values: form.values,
          mission: form.mission,
          alignment: form.alignment,
          prompt:
            form.ai_experience_prompt ||
            "Write a concise paragraph from a high-school counselor describing specific experiences working with the student (academic planning, leadership, service), professional tone.",
          perspective: "high-school-counselor",
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "AI generation failed");

      const text = (data.experienceText || "").trim();
      const withTail = addEndorsementTail(text, { studentName: form.student, college: form.college, program: form.program });

      setForm((f) => ({ ...f, experience: withTail, include_experience: true }));
      if (withTail) pushHistory("experience", withTail);

      setLetters((prev) => {
        if (!prev || !Object.keys(prev).length) return prev;
        const next = {};
        for (const [tid, entry] of Object.entries(prev)) {
          let newText = entry.orig || entry.text || "";
          newText = injectExperienceIntoLetter(newText, withTail, form);
          newText = injectSportIntoLetter(newText, form.sport, form.student);
          const edited = newText !== entry.orig;
          next[tid] = { ...entry, text: newText, edited, docxBlob: edited ? null : entry.docxBlob || null };
        }
        return next;
      });

      requestAnimationFrame(() => {
        setAiGlow(true);
        setTimeout(() => setAiGlow(false), 1600);
      });
    } catch (err) {
      setError(err.message || "Something went wrong generating experience.");
    } finally {
      setAiBusy(false);
    }
  }

  useEffect(() => {
    if (!Object.keys(letters).length) return;
    setLetters((prev) => {
      const next = {};
      const expWithTail = addEndorsementTail(form.experience, {
        studentName: form.student,
        college: form.college,
        program: form.program,
      });
      for (const [tid, entry] of Object.entries(prev)) {
        let base = entry.orig || entry.text || "";
        base = form.include_experience
          ? injectExperienceIntoLetter(base, expWithTail, form)
          : removeExperienceFromLetter(base, expWithTail);
        base = injectSportIntoLetter(base, form.sport, form.student);
        const edited = base !== entry.orig;
        next[tid] = { ...entry, text: base, edited, docxBlob: edited ? null : entry.docxBlob || null };
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ form.experience, form.include_experience, form.closing, form.sport, form.student ]);

  const updateFromMulti = (key, allOptions, selected = [], mode) => {
    const current = mode === "comma" ? parseComma(form[key]) : mode === "reasons" ? parseReasons(form[key]) : parseSemicolon(form[key]);
    const optionSet = new Set(allOptions.map(toLower));
    const custom = current.filter((x) => !optionSet.has(toLower(x)));
    const combined = uniqKeepOrder([...(selected || []), ...custom]);
    const joined = mode === "comma" ? combined.join(", ") : mode === "reasons" ? combined.join(";\n") : combined.join("; ");
    setForm((f) => ({ ...f, [key]: joined }));
  };

  const appendToDelimitedField = (key, value, mode) => {
    const val = String(value || "").trim();
    if (!val) return;
    let parts = mode === "comma" ? parseComma(form[key]) : mode === "reasons" ? parseReasons(form[key]) : parseSemicolon(form[key]);
    if (!parts.map(toLower).includes(toLower(val))) parts.push(val);
    const joined = mode === "comma" ? parts.join(", ") : mode === "reasons" ? parts.join(";\n") : parts.join("; ");
    setForm((f) => ({ ...f, [key]: joined }));
    pushHistory(key, joined);
    addCustomOption(key, val);
  };

  const selectedAchievements = intersectOptions(achievementOpts, parseSemicolon(form.achievements));
  const selectedHard = intersectOptions(hardOpts, parseComma(form.hard_skills));
  const selectedSoft = intersectOptions(softOpts, parseComma(form.soft_skills));
  const selectedReasons = intersectOptions(reasonOpts, parseReasons(form.reasons));

  const previewForm = useMemo(() => ({ ...form, header_line1: "", header_line2: "", header_line3: "" }), [form]);

  if (auth.isLoading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Spinner className="h-10 w-10 text-teal-600" />
          <span className="font-medium text-gray-700">Authenticating...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-gray-100 to-gray-200 text-gray-700">
        {loading && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/10 backdrop-blur-sm"
            role="dialog"
            aria-modal="true"
            aria-label="Generating letter"
          >
            <div className="flex flex-col items-center gap-3 rounded-2xl bg-white p-5 shadow-2xl ring-1 ring-gray-200">
              <Spinner className="h-10 w-10 sm:h-12 sm:w-12 text-teal-600" />
              <span className="font-medium text-gray-700">Building letter…</span>
            </div>
          </div>
        )}

        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10">
          <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight bg-gradient-to-r from-teal-500 to-orange-500 bg-clip-text text-transparent">
                Letter Builder
              </h1>
              <p className="mt-2 text-gray-700 max-w-3xl">
                Generate polished recommendation letters in multiple <b>templates</b>—all beginning with a unified header (program,
                university, address, date).
              </p>
            </div>
          </header>

          <form
            onSubmit={onBuild}
            className="mt-8 grid lg:grid-cols-2 gap-8"
            aria-live="polite"
          >
            <div className="rounded-2xl border border-gray-200 bg-white p-6 sm:p-8 shadow-xl ring-1 ring-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold text-slate-900">Details</h2>
                <div className="flex items-center gap-4 text-sm">
                  <label className="inline-flex items-center gap-2">
                    <input type="radio" name="format" checked={form.format === "txt"} onChange={() => setField("format")({ target: { value: "txt" } })} className="accent-teal-600" />
                    TXT
                  </label>
                  <label className="inline-flex items-center gap-2">
                    <input type="radio" name="format" checked={form.format === "docx"} onChange={() => setField("format")({ target: { value: "docx" } })} className="accent-teal-600" />
                    DOCX
                  </label>
                </div>
              </div>

              <SavedLettersSection
                savedLettersList={savedLettersList}
                selectedSavedId={selectedSavedId}
                setSelectedSavedId={setSelectedSavedId}
                onLoad={loadSavedLetterById}
                onDelete={async (id) => {
                  try {
                    await remove(id);
                    const fresh = await list();
                    setSavedLettersList(fresh);
                    setSelectedSavedId("");
                  } catch {
                    setError("Failed to delete letter.");
                  }
                }}
              />

              <TemplatePicker
                TEMPLATE_CATALOG={TEMPLATE_CATALOG}
                selectedTemplates={selectedTemplates}
                setSelectedTemplates={setSelectedTemplates}
              />

              <UniversalHeaderSection
                form={form}
                setField={setField}
                pushHistory={pushHistory}
                history={history}
                todayLong={todayLong}
              />

              <CounselorContactSection
                form={form}
                setField={setField}
                pushHistory={pushHistory}
                history={history}
              />

              <StudentInstitutionSection
                form={form}
                setField={setField}
                pushHistory={pushHistory}
                history={history}
                colleges={colleges}
                majorsByCategory={majorsByCategory}
                SPORT_OPTIONS={SPORT_OPTIONS}
              />

              {selectedTemplates.includes("professional") && (
                <RecipientSection
                  form={form}
                  setField={setField}
                  pushHistory={pushHistory}
                  history={history}
                />
              )}

              <ExperienceSection
                form={form}
                setField={setField}
                pushHistory={pushHistory}
                history={history}
                aiExperience={aiExperience}
                aiBusy={aiBusy}
                aiGlow={aiGlow}
                aiPresets={AI_EXPERIENCE_PRESETS}
              />

              <ClosingSection form={form} setField={setField} />

              <div className="pt-4 flex flex-wrap items-center gap-3">
                <button
                  type="submit"
                  className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-teal-600 to-orange-600 px-4 py-2 text-white font-semibold shadow-md hover:from-teal-700 hover:to-orange-700 focus:outline-none focus:ring-2 focus:ring-teal-600/40 disabled:opacity-50"
                  disabled={loading}
                >
                  Generate Letter(s)
                </button>
                {error && (
                  <span
                    className="text-sm text-orange-700"
                    role="alert"
                    aria-live="assertive"
                  >
                    {error}
                  </span>
                )}
              </div>
            </div>

            {/* Right: Preview */}
            <PreviewSection
              letters={letters}
              activeTemplateId={activeTemplateId}
              setActiveTemplateId={setActiveTemplateId}
              TEMPLATE_CATALOG={TEMPLATE_CATALOG}
              currentLetter={currentLetter}
              currentDocx={currentDocx}
              isCurrentEdited={isCurrentEdited}
              setLetterText={setLetterText}
              revertActive={revertActive}
              form={previewForm}
              onSaveLetter={onSaveLetter}
              savedAt={savedAt}
            />
          </form>
        </div>
      </div>
    </>
  );
}
